<template>
    <div>
        <div class="row">
            <div class="col-md-6 d-md-flex align-items-baseline">
                <h1 class="mr-2">List of Discounts</h1>
            </div>
            <div class="col-md-6 pb-3">
                <input-search-icon
                    v-model="filter.search"
                    @search="handleSearch"
                    placeholder="Search Discounts"
                    :debouncetime="50"
                >
                    <template v-slot:before>
                        <dropdown
                            class="pr-2"
                            :btnContent="`Status: ${filter.status.label}`"
                            :itemList="statusList"
                            @selected="changeStatus"
                        ></dropdown>
                    </template>

                    <template v-slot:after>
                        <button
                            type="button"
                            class="btn btn-secondary ml-1"
                            v-b-tooltip.hover.top
                            title="New Discount"
                            @click="() => $refs.modalCreateUpdate.open()"
                            v-if="_hasAccess('setting.discount.add')"
                        >
                            <span class="fas fa-plus"></span>
                        </button>
                    </template>
                </input-search-icon>
            </div>
        </div>

        <div class="card rounded-lg p-3">
            <div class="card-body overflow-auto p-0">
                <div class="table-holder table-responsive">
                    <LoaderList v-if="isLoading" class="w-100" />
                    <table
                        v-else
                        class="table list"
                        :class="{
                            'table-hover': renderList.length > 0
                        }"
                    >
                        <thead>
                            <tr>
                                <th>
                                    <sorting
                                        text="Name:"
                                        :order-by="orderBy('name')"
                                        @sorting="val => sort(val, 'name')"
                                    >
                                    </sorting>
                                </th>
                                <th>Applies To:</th>
                                <th>
                                    <sorting
                                        text="Status:"
                                        :order-by="orderBy('status')"
                                        @sorting="val => sort(val, 'status')"
                                    >
                                    </sorting>
                                </th>
                                <th>
                                    <sorting
                                        text="Used:"
                                        :order-by="orderBy('used')"
                                        @sorting="val => sort(val, 'used')"
                                    >
                                    </sorting>
                                </th>
                                <th>Notes:</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody v-if="renderList.length > 0">
                            <tr v-for="item in renderList" :key="item.id">
                                <td>{{ item.name }}</td>
                                <td>{{ item.appliesToLabel }}</td>
                                <td>{{ item.statusLabel }}</td>
                                <td>{{ item.usedLabel }}</td>
                                <td>{{ item.notes }}</td>
                                <td v-if="item.status === 3"></td>
                                <td v-else class="text-right small text-nowrap">
                                    <span
                                        class="cursor-pointer"
                                        @click="() => edit(item.id)"
                                        v-if="
                                            _hasAccess(
                                                'setting.discount.update'
                                            )
                                        "
                                    >
                                        Edit
                                    </span>
                                    <span v-if="item.used && hasAccessDelete">
                                        |
                                        <span
                                            class="cursor-pointer"
                                            @click="
                                                $refs.modalDeleteDisable.open({
                                                    isDisableMode: true,
                                                    id: item.id
                                                })
                                            "
                                        >
                                            Disable
                                        </span>
                                    </span>
                                    <span
                                        v-else-if="hasAccessDelete"
                                        class="cursor-pointer"
                                    >
                                        |
                                        <span
                                            class="cursor-pointer"
                                            @click="
                                                $refs.modalDeleteDisable.open({
                                                    isDeleteMode: true,
                                                    id: item.id
                                                })
                                            "
                                        >
                                            Delete
                                        </span>
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                        <tbody v-else>
                            <tr>
                                <td colspan="6">
                                    <i>No records available</i>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <modal-delete-disable
            ref="modalDeleteDisable"
            @deleted="postDelete"
            @disabled="postDisable"
        />
        <modal-create-update ref="modalCreateUpdate" @success="fetchList" />
    </div>
</template>

<script>
export default {
    components: {
        inputSearchIcon: () => import("@/module/commons/input-search-icon.vue"),
        LoaderList: () => import("@/components/elements/loader-list.vue"),
        Sorting: () => import("@/module/commons/sorting.vue"),
        Dropdown: () => import("@/components/commons/dropdown.vue"),
        ModalDeleteDisable: () =>
            import("@/module/settings/discounts/modal-delete-disable.vue"),
        ModalCreateUpdate: () =>
            import("@/module/settings/discounts/modal-create-update.vue")
    },
    data() {
        return {
            isLoading: true,
            filter: {
                search: "",
                status: {
                    label: "All",
                    value: null
                }
            },
            sorting: {
                column: "name",
                order: null
            },
            totalPages: 0,
            list: [],
            renderList: [],
            categories: [],
            allCategories: "",
            statusList: [
                {
                    label: "All",
                    value: null
                },
                {
                    label: "Active",
                    value: 1
                },
                {
                    label: "Disabled",
                    value: 3
                }
            ]
        };
    },
    computed: {
        hasAccessDelete() {
            return this._hasAccess("setting.discount.delete");
        }
    },
    async mounted() {
        await this.fetchCategories();
        this.fetchList();
    },
    methods: {
        postDelete(id) {
            // remove from list
            this.renderList = this.renderList.filter(
                record => record.id !== id
            );
        },
        postDisable(id) {
            let record = this.renderList.find(rec => rec.id === id);
            if (record) {
                record.status = 3; // change to disabled
                record.statusLabel = "Disabled";
            }
        },
        async fetchCategories() {
            const res = await this.$http.get(
                `${process.env.BARRAMUNDI}inventory/category-by-type`
            );
            let raw = res?.data?.response?.resources ?? [];
            let cats = raw.flatMap(item => item.category);

            this.categories = cats;
            this.allCategories = cats.map(item => item.name).join(", ");
        },
        async fetchList(params = {}) {
            this.isLoading = true;

            const res = await this.$http.get(
                `${process.env.BARRAMUNDI}account/discount`,
                {
                    params
                }
            );

            let mod = [];
            let raw = res?.data?.response?.resource?.data ?? [];

            for (let data of raw) {
                let used =
                    data?.total_used?.toUpperCase() === "YES" ? true : false;

                let notes = data.notes;
                if (notes === "") {
                    notes = "-";
                }

                let appliesToItems = "";
                let appliesToLabel = this.appliesToLabel(data);

                if (data.all_category === 1) {
                    appliesToItems = this.allCategories;
                } else {
                    appliesToItems = appliesToLabel;
                }

                mod.push({
                    id: data.id,
                    name: data.name,
                    appliesToItems,
                    appliesToLabel,
                    status: data.status,
                    statusLabel: this.statusLabel(data.status),
                    used: used,
                    usedLabel: used ? "Yes" : "No",
                    notes: notes
                });
            }

            this.list = mod;
            this.renderList = mod;
            this.isLoading = false;
        },
        statusLabel(code) {
            switch (code) {
                case 1:
                    return "Active";

                case 2:
                    return "Inactive";

                case 3:
                    return "Disabled";
            }
            return "-";
        },
        appliesToLabel(item) {
            if (item.all_category === 1) {
                return "All categories";
            }

            let arr = [];
            for (let category of item?.discount_categories) {
                let item = this.categories.find(
                    cat => cat.id === category.category_id
                );
                if (item) {
                    arr.push(item.name);
                }
            }

            return arr.join(", ").trim();
        },
        sort(value, column) {
            this.sorting = {
                column: column,
                order: value
            };

            this.fetchList({
                order: value,
                sort: column,
                status: this.filter.status.value
            });
        },
        changeStatus({ label, value }) {
            this.filter.status = {
                label: label,
                value: value
            };

            this.fetchList({
                order: this.sorting.order,
                sort: this.sorting.column,
                status: value
            });
        },
        orderBy(column) {
            if (this.sorting.column === column) {
                return this.sorting.order;
            }
            return null;
        },
        handleSearch(keyword = "") {
            if (keyword !== "") {
                this.renderList = this.searchByKeyword(keyword);
            } else {
                this.renderList = this.list;
            }
        },
        searchByKeyword(keyword = "") {
            const lowerCaseKeyword = keyword.toLowerCase();

            return this.list.filter(item => {
                if (
                    item.appliesToItems
                        ?.toLowerCase()
                        .includes(lowerCaseKeyword)
                ) {
                    return true;
                }

                if (item.name?.toLowerCase().includes(lowerCaseKeyword)) {
                    return true;
                }

                if (item.notes?.toLowerCase().includes(lowerCaseKeyword)) {
                    return true;
                }

                return false;
            });
        },
        edit(id) {
            this.$refs.modalCreateUpdate.open({
                isEditMode: true,
                id: id
            });
        }
    }
};
</script>
